import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { announcementActions } from "../slices/announcement";
import { AnnouncementMapper } from "../../mappers/AnnouncementMapper";
import { getErrorMessage } from "../../utils/errorUtils";
import { AnnouncementRequestDto } from "../../shared/dtos/announcement/AnnouncementRequestDto";
import { AnnouncementResponseDto } from "../../shared/dtos/announcement/AnnouncementResponseDto";
import { AnnouncementSummaryDto } from "../../shared/dtos/announcement/AnnouncementSummaryDto";
import { AnnouncementQueryDto } from "../../shared/dtos/announcement/AnnouncementQueryDto";
import { UpdateAnnouncementPayload } from "../../shared/interfaces/announcement/UpdateAnnouncementPayload";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const createAnnouncement =
  (values: AnnouncementRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(announcementActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<AnnouncementResponseDto>
      >("/announcements", values, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Announcement created successfully!");
        const summary = AnnouncementMapper.mapAnnouncementToSummary(data.data);
        dispatch(announcementActions.createSuccess(summary));
        dispatch(
          announcementActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(announcementActions.setErrorCreate(message));
    }
  };

export const getAnnouncements =
  (params?: AnnouncementQueryDto) => async (dispatch: AppDispatch) => {
    dispatch(announcementActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<AnnouncementSummaryDto>>
      >("/announcements", { headers: { requiresAuth: true }, params });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(announcementActions.setItems(data.data.content));
        dispatch(announcementActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(announcementActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getAnnouncementById =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(announcementActions.setLoadingFetchOne());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<AnnouncementResponseDto>
      >(`/announcements/${id}`, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(announcementActions.setItem(data.data));
      }
    } catch (error) {
      dispatch(announcementActions.setErrorFetchOne(getErrorMessage(error)));
    }
  };

export const updateAnnouncement =
  (payload: UpdateAnnouncementPayload) => async (dispatch: AppDispatch) => {
    dispatch(announcementActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<AnnouncementResponseDto>
      >(`/announcements/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Announcement updated successfully!");
        const summary = AnnouncementMapper.mapAnnouncementToSummary(data.data);
        dispatch(announcementActions.updateSuccess(summary));
        dispatch(
          announcementActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(announcementActions.setErrorUpdate(message));
    }
  };

export const deleteAnnouncement =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(announcementActions.setLoadingDelete());
    try {
      const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
        `/announcements/${id}`,
        { headers: { requiresAuth: true } },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Announcement deleted successfully!");
        dispatch(announcementActions.deleteSuccess(id));
        dispatch(
          announcementActions.setServerStatus({
            operation: "delete",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(announcementActions.setErrorDelete(message));
    }
  };

export const resetAnnouncementServerStatus = () => (dispatch: AppDispatch) => {
  dispatch(announcementActions.setServerStatus(null));
};
